import { isPartiallyPaidState } from "./invoiceStateUtils";
import { isSalesDocument } from "./invoiceTypeLogic";

// =========================================================
// SECTION 1: Core Numeric + Id Helpers
// =========================================================
const EPSILON = 0.02;

const toNum = (value: any): number => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const n = parseFloat(raw.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n: number): number => Math.round(n * 100) / 100;

const isLongId = (value: any): boolean => /^\d+$/.test(String(value ?? "").trim());
const asLongId = (value: any): string | undefined =>
  isLongId(value) ? String(value).trim() : undefined;

const asIntString = (value: any): string | undefined => {
  const raw = String(value ?? "").trim();
  return /^-?\d+$/.test(raw) ? raw : undefined;
};

const getCurrencyRate = (invoice: any): number => {
  const rate = toNum(invoice?.currencyRate);
  return rate > 0 ? rate : 1;
};

const resolveInvoiceSubject = (invoice: any): string | undefined => {
  const direct = asLongId(invoice?.subject);
  if (direct) return direct;

  const byDocSide = isSalesDocument(invoice)
    ? asLongId(invoice?.customer)
    : asLongId(invoice?.provider);

  return (
    byDocSide ||
    asLongId(invoice?.customer) ||
    asLongId(invoice?.provider) ||
    asLongId(invoice?.folder) ||
    asLongId(invoice?.shipTo) ||
    asLongId(invoice?.project) ||
    asLongId(invoice?.userId)
  );
};

const normalizeCurrencyCode = (value: any, fallback?: string): string => {
  const raw = String(value ?? "").trim();
  if (!raw) return String(fallback ?? "");
  // backend expects currency code (e.g. CZK/USD), not internal numeric id
  if (/^\d+$/.test(raw)) return String(fallback ?? "");
  return raw;
};

// PDF: Original currency value = Bookkeeping currency * Exchange rate
// => bookkeeping = original / rate
const toBookkeepingAmount = (amountOriginal: number, rate: number): number =>
  round2(amountOriginal / rate);

const hasValue = (value: any): boolean => String(value ?? "").trim() !== "";

/**
 * Sign source matrix (invoice/finance integration contract)
 *
 * 1) Read/normalize mode (useResponseAmounts = true):
 * - Keep response signs when backend sent values.
 * - Only calculate missing values as fallback.
 *
 * 2) Save mode for Add/Edit (useResponseAmounts = false):
 * - Recalculate transaction/header amounts from current editable rows.
 * - Keep accounting entry balanced (SUM bookkeepingAmount = 0).
 *
 * VATTitle behavior:
 * - N (netto line): main row transaction, item row in grid.
 * - T (tax line): tax for N line (linked by parent/childKey).
 * - R (rounding): optional extra row, preserved if present.
 * - S (summary): exactly one balancing row per invoice.
 *
 * Reverted fields:
 * - bookkeepingAmountReverted is opposite-sign presentation value.
 * - In response mode: prefer response value.
 * - In save mode: derive from recalculated bookkeepingAmount.
 *
 * Shares:
 * - For VATTitle=N controlling shares are opposite sign vs transaction amounts.
 * - Validation enforces share sums against transaction amounts.
 */
const resolveRevertedAmount = (existingReverted: any, fallbackAmount: number): string => {
  const raw = String(existingReverted ?? "").trim();
  if (raw !== "") return String(toNum(existingReverted));
  return String(fallbackAmount);
};

const detectAmountSign = (...values: any[]): number | null => {
  for (const value of values) {
    const n = toNum(value);
    if (n > 0) return 1;
    if (n < 0) return -1;
  }
  return null;
};

const getLineTransactions = (invoice: any): any[] => {
  const list = Array.isArray(invoice?.TRANSACTION) ? invoice.TRANSACTION : [];
  return list.filter((txn: any) => {
    if (String(txn?.remove ?? "0") === "1") return false;
    const vatTitle = String(txn?.VATTitle ?? "").toUpperCase();
    if (vatTitle && vatTitle !== "N") return false;

    const hasName = String(txn?.itemName ?? txn?.itemDescription ?? "").trim().length > 0;
    const qty = toNum(txn?.itemQuantity);
    const unit = toNum(txn?.otherUnitPrice ?? txn?.catalogPrice ?? txn?.bookkeepingUnitPrice);
    return hasName || qty > 0 || unit !== 0;
  });
};

const getPrimaryEntryId = (invoice: any): string => {
  const list = Array.isArray(invoice?.TRANSACTION) ? invoice.TRANSACTION : [];
  const firstWithEntry = list.find((txn: any) => String(txn?.entry ?? "").trim().length > 0);
  return String(firstWithEntry?.entry ?? "");
};

type Share = Record<string, any>;

const toSharesArray = (txn: any): Share[] => {
  const raw = txn?.SHARES?.SHARE;
  const shares = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return shares.filter((s) => s != null);
};

const setSharesArray = (txn: any, shares: Share[]) => ({
  ...txn,
  SHARES: { SHARE: shares },
});

// =========================================================
// SECTION 2: Share Handling (Create/Update Safety)
// - Ensures shares exist
// - Balances share totals against NETTO (N) transaction
// =========================================================
const ensureTransactionShares = (txn: any, invoice: any) => {
  const shares = toSharesArray(txn);
  if (shares.length > 0) return txn;

  const vatTitle = String(txn?.VATTitle ?? "").toUpperCase();
  const txnOtherAmount = toNum(txn?.otherAmount);
  const txnBookAmount = toNum(txn?.bookkeepingAmount);

  // Shares are mainly expected for N transactions; sign is opposite for controlling data.
  const shareTotal =
    vatTitle === "N"
      ? round2(-(txnOtherAmount || txnBookAmount))
      : round2(txnOtherAmount || txnBookAmount);

  const shareBookKeepingAmount =
    vatTitle === "N"
      ? round2(-(txnBookAmount || txnOtherAmount))
      : round2(txnBookAmount || txnOtherAmount);

  const share: Share = {
    GLItem: asLongId(txn?.id),
    GLItemName: txn?.itemName || txn?.itemDescription || "",
    templateAcc: txn?.templateAcc || invoice?.templateAcc,
    treeHandle: txn?.treeHandle || invoice?.transactionTreeHandle || invoice?.treeHandle,
    userId: txn?.userId || invoice?.userId,
    userName: txn?.userName || invoice?.userName,
    folder: txn?.folder || invoice?.folder,
    project: txn?.project || invoice?.project,
    activity: txn?.activity || invoice?.activity,
    subject:
      asLongId(txn?.subject) ||
      asLongId(txn?.folder) ||
      asLongId(invoice?.subject) ||
      asLongId(invoice?.customer) ||
      asLongId(invoice?.provider) ||
      asLongId(invoice?.folder) ||
      asLongId(invoice?.shipTo) ||
      asLongId(invoice?.project) ||
      asLongId(invoice?.userId),
    remove: "0",
    isRemaining: "1",
    units: "1",
    shareUnits: "1",
    percentage: "100",
    total: String(shareTotal),
    bookKeepingAmount: String(shareBookKeepingAmount),
    dimension1: txn?.dimension1 || invoice?.dimension1,
    dimension2: txn?.dimension2 || invoice?.dimension2,
    dimension3: txn?.dimension3 || invoice?.dimension3,
    dimension1Name: txn?.dimension1Name || invoice?.dimension1Name,
    dimension2Name: txn?.dimension2Name || invoice?.dimension2Name,
    dimension3Name: txn?.dimension3Name || invoice?.dimension3Name,
  };

  return setSharesArray(txn, [share]);
};

/**
 * PDF rule: sum(share.bookKeepingAmount) must equal transaction.bookkeepingAmount,
 * and for controlling shares we store opposite sign vs N transaction.
 *
 * For VATTitle="N": expected shares sum = - txn.bookkeepingAmount (and same for total vs otherAmount)
 * For others: we don't enforce (shares are usually for N only).
 */
const balanceTransactionShares = (txn: any, invoice: any) => {
  const vatTitle = String(txn?.VATTitle ?? "").toUpperCase();
  if (vatTitle !== "N") return txn;

  let next = ensureTransactionShares(txn, invoice);
  let shares = toSharesArray(next).map((s) => ({ ...s }));

  // Normalize defaults
  shares = shares.map((share: any) => ({
    ...share,
    treeHandle:
      asLongId(share?.treeHandle) ||
      asLongId(next?.treeHandle) ||
      asLongId(invoice?.transactionTreeHandle) ||
      asLongId(invoice?.treeHandle),
    folder:
      asLongId(share?.folder) ||
      asLongId(next?.folder) ||
      asLongId(invoice?.folder) ||
      asLongId(invoice?.customer) ||
      asLongId(invoice?.provider) ||
      asLongId(invoice?.shipTo) ||
      asLongId(invoice?.project) ||
      asLongId(invoice?.userId),
    subject:
      asLongId(share?.subject) ||
      asLongId(share?.folder) ||
      asLongId(next?.subject) ||
      asLongId(next?.folder) ||
      asLongId(invoice?.subject) ||
      asLongId(invoice?.customer) ||
      asLongId(invoice?.provider) ||
      asLongId(invoice?.folder) ||
      asLongId(invoice?.shipTo) ||
      asLongId(invoice?.project) ||
      asLongId(invoice?.userId),
    remove: share?.remove ?? "0",
    isRemaining: share?.isRemaining ?? "0",
    units: share?.units ?? "1",
    shareUnits: share?.shareUnits ?? "1",
    percentage: share?.percentage ?? "0",
  }));

  const activeShares = shares.filter((s: any) => String(s?.remove ?? "0") !== "1");

  const txnBook = round2(toNum(next?.bookkeepingAmount));
  const txnOrig = round2(toNum(next?.otherAmount));

  // Expected share sums are opposite sign for N
  const expectedShareBook = round2(-txnBook);
  const expectedShareOrig = round2(-txnOrig);

  const sumShareBook = round2(activeShares.reduce((acc: number, s: any) => acc + toNum(s?.bookKeepingAmount), 0));
  const sumShareOrig = round2(activeShares.reduce((acc: number, s: any) => acc + toNum(s?.total), 0));

  const diffBook = round2(expectedShareBook - sumShareBook);
  const diffOrig = round2(expectedShareOrig - sumShareOrig);

  // If already balanced, keep as is
  if (Math.abs(diffBook) <= EPSILON && Math.abs(diffOrig) <= EPSILON) {
    return setSharesArray(next, shares);
  }

  // Prefer adjusting an existing "remaining" share
  const idxRemaining = shares.findIndex(
    (s: any) => String(s?.remove ?? "0") !== "1" && String(s?.isRemaining ?? "0") === "1"
  );

  const remainderShareBase: Share =
    idxRemaining >= 0
      ? shares[idxRemaining]
      : {
          GLItem: asLongId(next?.id),
          GLItemName: next?.itemName || next?.itemDescription || "",
          templateAcc: next?.templateAcc || invoice?.templateAcc,
          treeHandle: next?.treeHandle || invoice?.transactionTreeHandle || invoice?.treeHandle,
          userId: next?.userId || invoice?.userId,
          userName: next?.userName || invoice?.userName,
          folder: asLongId(next?.folder) || asLongId(invoice?.folder),
          project: next?.project || invoice?.project,
          activity: next?.activity || invoice?.activity,
          subject: asLongId(next?.subject) || resolveInvoiceSubject(invoice),
          remove: "0",
          isRemaining: "1",
          units: "1",
          shareUnits: "1",
          percentage: "0",
          dimension1: next?.dimension1 || invoice?.dimension1,
          dimension2: next?.dimension2 || invoice?.dimension2,
          dimension3: next?.dimension3 || invoice?.dimension3,
          dimension1Name: next?.dimension1Name || invoice?.dimension1Name,
          dimension2Name: next?.dimension2Name || invoice?.dimension2Name,
          dimension3Name: next?.dimension3Name || invoice?.dimension3Name,
        };

  const updatedRemainder = {
    ...remainderShareBase,
    isRemaining: "1",
    total: String(round2(toNum(remainderShareBase?.total) + diffOrig)),
    bookKeepingAmount: String(round2(toNum(remainderShareBase?.bookKeepingAmount) + diffBook)),
  };

  if (idxRemaining >= 0) {
    shares[idxRemaining] = updatedRemainder;
  } else {
    shares.push(updatedRemainder);
  }

  return setSharesArray(next, shares);
};

const normalizeTransactionSharesForCreate = (txn: any, invoice: any) => {
  const shares = toSharesArray(txn);

  if (!shares.length) {
    return ensureTransactionShares(txn, invoice);
  }

  const normalizedShares = shares.map((share: any) => ({
    ...share,
    treeHandle:
      asLongId(share?.treeHandle) ||
      asLongId(txn?.treeHandle) ||
      asLongId(invoice?.transactionTreeHandle) ||
      asLongId(invoice?.treeHandle),
    folder:
      asLongId(share?.folder) ||
      asLongId(txn?.folder) ||
      asLongId(invoice?.folder) ||
      asLongId(invoice?.customer) ||
      asLongId(invoice?.provider) ||
      asLongId(invoice?.shipTo) ||
      asLongId(invoice?.project) ||
      asLongId(invoice?.userId),
    subject:
      asLongId(share?.subject) ||
      asLongId(share?.folder) ||
      asLongId(txn?.subject) ||
      asLongId(txn?.folder) ||
      asLongId(invoice?.subject) ||
      asLongId(invoice?.customer) ||
      asLongId(invoice?.provider) ||
      asLongId(invoice?.folder) ||
      asLongId(invoice?.shipTo) ||
      asLongId(invoice?.project) ||
      asLongId(invoice?.userId),
    remove: share?.remove ?? "0",
    isRemaining: share?.isRemaining ?? "0",
    units: share?.units ?? "1",
    shareUnits: share?.shareUnits ?? "1",
    percentage: share?.percentage ?? "0",
  }));

  return setSharesArray(txn, normalizedShares);
};

const buildStrictTransactions = (
  invoice: any,
  options?: { useResponseAmounts?: boolean }
) => {
  // =======================================================
  // SECTION 3: Transaction Builder
  // - Rebuilds N/T/R/S transactions into strict structure
  // - Computes totals and keeps accounting entry balanced
  // =======================================================
  // useResponseAmounts mode map:
  // - true  => Read/normalize mode (prefer amounts from backend response).
  // - false => Add/Edit save mode (recalculate amounts at runtime from current rows).
  const useResponseAmounts = options?.useResponseAmounts ?? true;
  const isSalesDoc = isSalesDocument(invoice);
  const lines = getLineTransactions(invoice);
  const existing = Array.isArray(invoice?.TRANSACTION) ? invoice.TRANSACTION : [];
  const existingTByParent = new Map<string, any>();
  const existingRounding: any[] = [];
  const removedTransactions: any[] = [];
  let existingSummary: any = null;

  existing.forEach((txn: any) => {
    if (String(txn?.remove ?? "0") === "1") {
      removedTransactions.push(txn);
      return;
    }
    const vatTitle = String(txn?.VATTitle ?? "").toUpperCase();
    if (vatTitle === "T" && txn?.parent != null) {
      existingTByParent.set(String(txn.parent), txn);
    } else if (vatTitle === "T" && txn?.childKey) {
      existingTByParent.set(String(txn.childKey), txn);
    } else if (vatTitle === "R") {
      existingRounding.push(txn);
    } else if (vatTitle === "S" && !existingSummary) {
      existingSummary = txn;
    }
  });

  const defaultLineSign = (() => {
    const existingN = existing.filter(
      (txn: any) =>
        String(txn?.remove ?? "0") !== "1" &&
        String(txn?.VATTitle ?? "").toUpperCase() === "N"
    );
    for (const txn of existingN) {
      const sign = detectAmountSign(
        txn?.otherAmount,
        txn?.bookkeepingAmount,
        txn?.bookkeepingAmountReverted,
        txn?.otherUnitPrice,
        txn?.catalogPrice,
        txn?.bookkeepingUnitPrice
      );
      if (sign) return sign;
    }
    return null;
  })();
  const rate = getCurrencyRate(invoice);
  const transactionDate = invoice?.dateTransaction || invoice?.taxationDate || invoice?.issuedDate;
  const currency = invoice?.currency || "";
  const entryId = getPrimaryEntryId(invoice);
  const defaultType = "1";

  let sumOriginal = 0;
  let sumBookkeeping = 0;

  // These totals are computed in "human" positive space.
  // Later we will apply PDF storage sign for purchase headers.
  let totalExclHuman = 0;
  let totalInclHuman = 0;

  const result: any[] = [];

  lines.forEach((line: any, index: number) => {
    // -----------------------------------------------------
    // 3A) Build NETTO line (VATTitle = N)
    // -----------------------------------------------------
    const qty = Math.max(0, toNum(line?.itemQuantity));
    const rawUnitPrice = toNum(
      line?.otherUnitPrice ?? line?.catalogPrice ?? line?.bookkeepingUnitPrice
    );
    const vatPct = Math.max(0, toNum(line?.VATValue ?? line?.vatValue));
    const lineSign =
      detectAmountSign(
        rawUnitPrice,
        line?.otherAmount,
        line?.bookkeepingAmount,
        line?.bookkeepingAmountReverted
      ) ?? defaultLineSign;
    const unitPriceBase =
      rawUnitPrice !== 0
        ? round2(rawUnitPrice)
        : round2(toNum(line?.catalogPrice ?? line?.bookkeepingUnitPrice ?? line?.otherUnitPrice));
    const unitPrice = useResponseAmounts ? unitPriceBase : Math.abs(unitPriceBase);

    const excl = round2(qty * unitPrice);
    const tax = round2((excl * vatPct) / 100);
    const incl = round2(excl + tax);

    const storageSign = useResponseAmounts
      ? (lineSign ?? 1)
      : (isSalesDoc ? -1 : 1);

    const signExcl = round2(excl * storageSign);
    const signTax = round2(tax * storageSign);
    const signedUnitPrice = round2(unitPrice * storageSign);

    const exclBookCalculated = toBookkeepingAmount(signExcl, rate);
    const taxBookCalculated = toBookkeepingAmount(signTax, rate);
    const exclBook =
      useResponseAmounts && hasValue(line?.bookkeepingAmount)
        ? round2(toNum(line?.bookkeepingAmount))
        : exclBookCalculated;

    totalExclHuman = round2(totalExclHuman + excl);
    totalInclHuman = round2(totalInclHuman + incl);

    const lineNumericId = asLongId(line?.id);
    const lineKey = String(lineNumericId || line?.childKey || line?.parentKey || `ROW_${index + 1}`);
    const originalOrderIndex = toNum(line?.orderIndex);
    const orderIndex = originalOrderIndex >= 9999 ? index : originalOrderIndex;

    const netTxn = {
      ...line,
      id: lineNumericId,
      templateAcc: line?.templateAcc || invoice?.templateAcc,
      orderIndex: String(orderIndex),
      VATTitle: "N",
      VAT: String(line?.VAT ?? line?.vatLabel ?? ""),
      VATValue: vatPct,
      itemQuantity: String(qty),
      otherUnitPrice:
        useResponseAmounts && hasValue(line?.otherUnitPrice)
          ? String(round2(toNum(line?.otherUnitPrice)))
          : String(signedUnitPrice),
      otherAmount:
        useResponseAmounts && hasValue(line?.otherAmount)
          ? String(round2(toNum(line?.otherAmount)))
          : String(signExcl),
      otherCurrency: normalizeCurrencyCode(line?.otherCurrency, currency),
      otherCurrencyRate: String(rate),
      bookkeepingAmount: String(exclBook),
      bookkeepingAmountReverted:
        resolveRevertedAmount(line?.bookkeepingAmountReverted, -exclBook),
      bookkeepingUnitPrice:
        useResponseAmounts && hasValue(line?.bookkeepingUnitPrice)
          ? String(round2(toNum(line?.bookkeepingUnitPrice)))
          : String(signedUnitPrice),
      bookkeepingCurrency: line?.bookkeepingCurrency || currency,
      bookkeepingCurrencyRate: String(rate),
      transactionDate: line?.transactionDate || transactionDate,
      entry: asLongId(line?.entry) || asLongId(entryId),
      journal: line?.journal || invoice?.journal,
      treeHandle: line?.treeHandle || invoice?.transactionTreeHandle || invoice?.treeHandle,
      type: line?.type || defaultType,
      account: line?.account || invoice?.accountBase,
      accountName: line?.accountName || invoice?.accountBaseName,
      userId: line?.userId || invoice?.userId,
      userName: line?.userName || invoice?.userName,
      project: line?.project || invoice?.project,
      projectName: line?.projectName || invoice?.projectName,
      folder: line?.folder || invoice?.folder,
      folderName: line?.folderName || invoice?.folderName,
      dimension1: line?.dimension1 || invoice?.dimension1,
      dimension1Name: line?.dimension1Name || invoice?.dimension1Name,
      dimension2: line?.dimension2 || invoice?.dimension2,
      dimension2Name: line?.dimension2Name || invoice?.dimension2Name,
      dimension3: line?.dimension3 || invoice?.dimension3,
      dimension3Name: line?.dimension3Name || invoice?.dimension3Name,
      parent: asLongId(line?.parent),
      parentId: asLongId(line?.parentId),
      subject: asLongId(line?.subject) || asLongId(line?.folder) || resolveInvoiceSubject(invoice),
      parentKey: line?.parentKey,
      childKey: line?.childKey || lineKey,
    };
    result.push(netTxn);

    sumOriginal = round2(sumOriginal + toNum(netTxn.otherAmount));
    sumBookkeeping = round2(sumBookkeeping + exclBook);

    if (vatPct > 0) {
      // ---------------------------------------------------
      // 3B) Build TAX line (VATTitle = T) linked to N line
      // ---------------------------------------------------
      const existingT = existingTByParent.get(lineNumericId || lineKey);
      const taxBook =
        useResponseAmounts && hasValue(existingT?.bookkeepingAmount)
          ? round2(toNum(existingT?.bookkeepingAmount))
          : taxBookCalculated;
      const taxTxn = {
        ...existingT,
        id: asLongId(existingT?.id),
        templateAcc: existingT?.templateAcc || line?.templateAcc || invoice?.templateAcc,
        parent: asLongId(existingT?.parent) || lineNumericId,
        parentId: asLongId(existingT?.parentId),
        subject:
          asLongId(existingT?.subject) ||
          asLongId(existingT?.folder) ||
          asLongId(netTxn?.subject) ||
          resolveInvoiceSubject(invoice),
        orderIndex: String(orderIndex),
        VATTitle: "T",
        VAT:
          String(
            existingT?.VAT ??
            line?.VAT ??
            line?.vatLabel ??
            netTxn?.VAT ??
            ""
          ),
        VATValue: vatPct,
        itemQuantity: "1",
        otherUnitPrice:
          useResponseAmounts && hasValue(existingT?.otherUnitPrice)
            ? String(round2(toNum(existingT?.otherUnitPrice)))
            : "0",
        otherAmount:
          useResponseAmounts && hasValue(existingT?.otherAmount)
            ? String(round2(toNum(existingT?.otherAmount)))
            : String(signTax),
        otherCurrency: normalizeCurrencyCode(existingT?.otherCurrency, currency),
        otherCurrencyRate: String(rate),
        bookkeepingAmount: String(taxBook),
        bookkeepingAmountReverted:
          resolveRevertedAmount(existingT?.bookkeepingAmountReverted, -taxBook),
        bookkeepingCurrency: existingT?.bookkeepingCurrency || currency,
        bookkeepingCurrencyRate: String(rate),
        transactionDate: existingT?.transactionDate || transactionDate,
        splitSwitch: existingT?.splitSwitch || "2",
        account: existingT?.account || invoice?.accountVAT || invoice?.accountTotal,
        accountName: existingT?.accountName || invoice?.accountVATName || invoice?.accountTotalName,
        entry: asLongId(existingT?.entry) || asLongId(netTxn.entry) || asLongId(entryId),
        journal: existingT?.journal || invoice?.journal,
        treeHandle: existingT?.treeHandle || netTxn.treeHandle,
        type: existingT?.type || defaultType,
        userId: existingT?.userId || netTxn.userId,
        userName: existingT?.userName || netTxn.userName,
        project: existingT?.project || netTxn.project,
        projectName: existingT?.projectName || netTxn.projectName,
        folder: existingT?.folder || netTxn.folder,
        folderName: existingT?.folderName || netTxn.folderName,
        dimension1: existingT?.dimension1 || netTxn.dimension1,
        dimension1Name: existingT?.dimension1Name || netTxn.dimension1Name,
        dimension2: existingT?.dimension2 || netTxn.dimension2,
        dimension2Name: existingT?.dimension2Name || netTxn.dimension2Name,
        dimension3: existingT?.dimension3 || netTxn.dimension3,
        dimension3Name: existingT?.dimension3Name || netTxn.dimension3Name,
        childKey: existingT?.childKey || lineKey,
        parentKey: existingT?.parentKey || `TAX_${lineKey}`,
      };
      result.push(taxTxn);

      sumOriginal = round2(sumOriginal + toNum(taxTxn.otherAmount));
      sumBookkeeping = round2(sumBookkeeping + taxBook);
    }
  });

  // Preserve existing rounding rows (VATTitle = R) and include them in balance.
  existingRounding.forEach((roundingTxn: any, idx: number) => {
    const roundingOriginal = round2(toNum(roundingTxn?.otherAmount));
    const roundingBook = round2(toNum(roundingTxn?.bookkeepingAmount));

    const normalizedRounding = {
      ...roundingTxn,
      id: asLongId(roundingTxn?.id),
      VATTitle: "R",
      parentId: asLongId(roundingTxn?.parentId),
      subject: asLongId(roundingTxn?.subject) || asLongId(roundingTxn?.folder) || resolveInvoiceSubject(invoice),
      otherCurrency: normalizeCurrencyCode(roundingTxn?.otherCurrency, currency),
      bookkeepingCurrency: roundingTxn?.bookkeepingCurrency || currency,
      otherCurrencyRate: String(rate),
      bookkeepingCurrencyRate: String(rate),
      transactionDate: roundingTxn?.transactionDate || transactionDate,
      entry: asLongId(roundingTxn?.entry) || asLongId(entryId),
      journal: roundingTxn?.journal || invoice?.journal,
      treeHandle: roundingTxn?.treeHandle || invoice?.transactionTreeHandle || invoice?.treeHandle,
      type: roundingTxn?.type || defaultType,
      orderIndex: String(toNum(roundingTxn?.orderIndex ?? 9900 + idx)),
      account: roundingTxn?.account || invoice?.accountRound || invoice?.accountTotal,
      accountName: roundingTxn?.accountName || invoice?.accountRoundName || invoice?.accountTotalName,
      userId: roundingTxn?.userId || invoice?.userId,
      userName: roundingTxn?.userName || invoice?.userName,
      project: roundingTxn?.project || invoice?.project,
      projectName: roundingTxn?.projectName || invoice?.projectName,
      folder: roundingTxn?.folder || invoice?.folder,
      folderName: roundingTxn?.folderName || invoice?.folderName,
      dimension1: roundingTxn?.dimension1 || invoice?.dimension1,
      dimension1Name: roundingTxn?.dimension1Name || invoice?.dimension1Name,
      dimension2: roundingTxn?.dimension2 || invoice?.dimension2,
      dimension2Name: roundingTxn?.dimension2Name || invoice?.dimension2Name,
      dimension3: roundingTxn?.dimension3 || invoice?.dimension3,
      dimension3Name: roundingTxn?.dimension3Name || invoice?.dimension3Name,
      otherAmount: String(roundingOriginal),
      bookkeepingAmount: String(roundingBook),
      bookkeepingAmountReverted:
        resolveRevertedAmount(roundingTxn?.bookkeepingAmountReverted, -roundingBook),
      itemQuantity: String(toNum(roundingTxn?.itemQuantity ?? 1)),
      otherUnitPrice: String(toNum(roundingTxn?.otherUnitPrice ?? 0)),
    };

    result.push(normalizedRounding);
    sumOriginal = round2(sumOriginal + roundingOriginal);
    sumBookkeeping = round2(sumBookkeeping + roundingBook);

    // Rounding influences human total incl VAT (display)
    totalInclHuman = round2(totalInclHuman + roundingOriginal);
  });

  // Summary S transaction that balances the entry (sum bookkeeping = 0)
  const summaryOriginal =
    useResponseAmounts && hasValue(existingSummary?.otherAmount)
      ? round2(toNum(existingSummary?.otherAmount))
      : round2(-sumOriginal);
  const summaryBook =
    useResponseAmounts && hasValue(existingSummary?.bookkeepingAmount)
      ? round2(toNum(existingSummary?.bookkeepingAmount))
      : round2(-sumBookkeeping);

  const summaryTxn = {
    ...existingSummary,
    id: asLongId(existingSummary?.id),
    templateAcc: existingSummary?.templateAcc || invoice?.templateAcc,
    VATTitle: "S",
    parentId: asLongId(existingSummary?.parentId),
    subject: asLongId(existingSummary?.subject) || asLongId(existingSummary?.folder) || resolveInvoiceSubject(invoice),
    itemName: existingSummary?.itemName || "Invoice total",
    itemQuantity: "0",
    otherUnitPrice:
      useResponseAmounts && hasValue(existingSummary?.otherUnitPrice)
        ? String(round2(toNum(existingSummary?.otherUnitPrice)))
        : "0",
    otherAmount: String(summaryOriginal),
    pairingSum:
      useResponseAmounts && hasValue(existingSummary?.pairingSum)
        ? String(round2(toNum(existingSummary?.pairingSum)))
        : String(summaryOriginal),
    otherCurrency: normalizeCurrencyCode(existingSummary?.otherCurrency, currency),
    otherCurrencyRate: String(rate),
    bookkeepingAmount: String(summaryBook),
    bookkeepingAmountReverted:
      resolveRevertedAmount(existingSummary?.bookkeepingAmountReverted, -summaryBook),
    bookkeepingCurrency: existingSummary?.bookkeepingCurrency || currency,
    bookkeepingCurrencyRate: String(rate),
    transactionDate: existingSummary?.transactionDate || transactionDate,
    account: existingSummary?.account || invoice?.accountTotal,
    accountName: existingSummary?.accountName || invoice?.accountTotalName,
    splitSwitch: existingSummary?.splitSwitch || "2",
    entry: asLongId(existingSummary?.entry) || asLongId(entryId),
    journal: existingSummary?.journal || invoice?.journal,
    treeHandle: existingSummary?.treeHandle || invoice?.transactionTreeHandle || invoice?.treeHandle,
    type: existingSummary?.type || defaultType,
    userId: existingSummary?.userId || invoice?.userId,
    userName: existingSummary?.userName || invoice?.userName,
    project: existingSummary?.project || invoice?.project,
    projectName: existingSummary?.projectName || invoice?.projectName,
    folder: existingSummary?.folder || invoice?.folder,
    folderName: existingSummary?.folderName || invoice?.folderName,
    dimension1: existingSummary?.dimension1 || invoice?.dimension1,
    dimension1Name: existingSummary?.dimension1Name || invoice?.dimension1Name,
    dimension2: existingSummary?.dimension2 || invoice?.dimension2,
    dimension2Name: existingSummary?.dimension2Name || invoice?.dimension2Name,
    dimension3: existingSummary?.dimension3 || invoice?.dimension3,
    dimension3Name: existingSummary?.dimension3Name || invoice?.dimension3Name,
  };
  result.push(summaryTxn);

  // Keep explicitly removed transactions in payload (legacy behavior: remove=1).
  removedTransactions.forEach((txn: any) => {
    const persistedId = asLongId(txn?.id);
    if (!persistedId) return;
    result.push({
      ...txn,
      id: persistedId,
      remove: "1",
      parentId: asLongId(txn?.parentId),
      subject: asLongId(txn?.subject) || asLongId(txn?.folder) || resolveInvoiceSubject(invoice),
      treeHandle:
        asLongId(txn?.treeHandle) ||
        asLongId(invoice?.transactionTreeHandle) ||
        asLongId(invoice?.treeHandle),
      entry: asLongId(txn?.entry) || asLongId(entryId),
      journal: txn?.journal || invoice?.journal,
      transactionDate: txn?.transactionDate || transactionDate,
      otherCurrency: normalizeCurrencyCode(txn?.otherCurrency, currency),
      bookkeepingCurrency: txn?.bookkeepingCurrency || currency,
      otherCurrencyRate: String(rate),
      bookkeepingCurrencyRate: String(rate),
    });
  });

  const responseTotal =
    useResponseAmounts && hasValue(invoice?.total) ? round2(toNum(invoice?.total)) : null;
  const responseTotalWithVAT =
    useResponseAmounts && hasValue(invoice?.totalWithVAT) ? round2(toNum(invoice?.totalWithVAT)) : null;
  const responseToBePaid =
    useResponseAmounts && hasValue(invoice?.toBePaid) ? round2(toNum(invoice?.toBePaid)) : null;
  const headerSign = useResponseAmounts
    ? (detectAmountSign(responseTotalWithVAT, responseToBePaid, responseTotal, sumOriginal) ?? 1)
    : 1;
  const calculatedTotal = round2(headerSign * totalExclHuman);
  const calculatedTotalWithVAT = round2(headerSign * totalInclHuman);
  const calculatedToBePaid = calculatedTotalWithVAT;

  return {
    transactions: result,
    totals: {
      total: responseTotal ?? calculatedTotal,
      totalWithVAT: responseTotalWithVAT ?? calculatedTotalWithVAT,
      toBePaid: responseToBePaid ?? calculatedToBePaid,
      // optional: for UI use if needed
      _humanTotal: round2(totalExclHuman),
      _humanTotalWithVAT: round2(totalInclHuman),
    },
  };
};

const validateStrictTransactions = (transactions: any[]): string[] => {
  // =======================================================
  // SECTION 4: Structural Validation
  // - Entry balance (sum bookkeeping = 0)
  // - Exactly one summary S transaction
  // - Tax T exists for taxable N lines
  // =======================================================
  const errors: string[] = [];
  const activeTransactions = transactions.filter((txn: any) => String(txn?.remove ?? "0") !== "1");

  const sumBookkeeping = round2(
    activeTransactions.reduce((acc, txn) => acc + toNum(txn?.bookkeepingAmount), 0)
  );

  if (Math.abs(sumBookkeeping) > EPSILON) {
    errors.push("Accounting validation failed: transaction entry is not balanced.");
  }

  const summaryCount = activeTransactions.filter(
    (txn) => String(txn?.VATTitle ?? "").toUpperCase() === "S"
  ).length;
  if (summaryCount !== 1) {
    errors.push("Invoice must contain exactly one summary transaction (VATTitle = S).");
  }

  const entryIds = new Set(
    activeTransactions
      .map((txn) => String(txn?.entry ?? "").trim())
      .filter((entry) => entry.length > 0)
  );
  if (entryIds.size > 1) {
    errors.push("All invoice transactions must belong to the same accounting entry.");
  }

  const taxByParent = new Set(
    activeTransactions
      .filter((txn) => String(txn?.VATTitle ?? "").toUpperCase() === "T")
      .map((txn) => String(txn?.parent ?? ""))
  );

  activeTransactions
    .filter((txn) => String(txn?.VATTitle ?? "").toUpperCase() === "N")
    .forEach((netTxn) => {
      const vat = toNum(netTxn?.VATValue);
      const id = String(netTxn?.id ?? "");
      if (vat > 0 && !taxByParent.has(id)) {
        errors.push(`Missing tax transaction (T) for line id ${id}.`);
      }
    });

  return errors;
};

const validateShareSums = (transactions: any[]): string[] => {
  // =======================================================
  // SECTION 5: Share Validation
  // - For NETTO lines, share sums must match opposite sign
  // =======================================================
  const errors: string[] = [];
  const active = transactions.filter((t: any) => String(t?.remove ?? "0") !== "1");

  active.forEach((txn: any) => {
    const vatTitle = String(txn?.VATTitle ?? "").toUpperCase();
    if (vatTitle !== "N") return;

    const shares = toSharesArray(txn).filter((s: any) => String(s?.remove ?? "0") !== "1");
    if (!shares.length) {
      errors.push(`Missing controlling shares for NETTO transaction id ${String(txn?.id ?? "")}.`);
      return;
    }

    const txnBook = round2(toNum(txn?.bookkeepingAmount));
    const txnOrig = round2(toNum(txn?.otherAmount));

    // Shares are opposite sign for N
    const expectedBook = round2(-txnBook);
    const expectedOrig = round2(-txnOrig);

    const sumBook = round2(shares.reduce((acc: number, s: any) => acc + toNum(s?.bookKeepingAmount), 0));
    const sumOrig = round2(shares.reduce((acc: number, s: any) => acc + toNum(s?.total), 0));

    if (Math.abs(expectedBook - sumBook) > EPSILON || Math.abs(expectedOrig - sumOrig) > EPSILON) {
      errors.push(
        `Controlling shares do not match NETTO transaction id ${String(txn?.id ?? "")} (share sum mismatch).`
      );
    }
  });

  return errors;
};

const sanitizeLongFields = (target: any, keys: string[]) => {
  const next = { ...target };
  keys.forEach((key) => {
    if (next[key] === undefined || next[key] === null || next[key] === "") return;
    const normalized = asLongId(next[key]);
    if (normalized === undefined) {
      delete next[key];
    } else {
      next[key] = normalized;
    }
  });
  return next;
};

const sanitizeTransactionForSoap = (txn: any) => {
  // =======================================================
  // SECTION 6: SOAP Sanitize (Transaction)
  // - Normalize numeric/long fields before request
  // =======================================================
  let next = sanitizeLongFields(txn, [
    "id",
    "treeHandle",
    "createdBy",
    "modifiedBy",
    "account",
    "activity",
    "attrib0_id",
    "attrib1_id",
    "attrib2_id",
    "attrib3_id",
    "attrib4_id",
    "attrib5_id",
    "attrib6_id",
    "attrib7_id",
    "attrib8_id",
    "attrib9_id",
    "entry",
    "folder",
    "invoicedCat",
    "invoicedQty",
    "item",
    "itemComponentCategory",
    "journal",
    "ordertrn",
    "parent",
    "subject",
    "project",
    "recordId",
    "type",
    "userId",
  ]);

  if (next.orderIndex !== undefined) {
    const orderIndex = asIntString(next.orderIndex);
    if (orderIndex === undefined) delete next.orderIndex;
    else next.orderIndex = orderIndex;
  }
  if (next.VATValue !== undefined) {
    next.VATValue = toNum(next.VATValue);
  }

  // Sanitize shares numeric fields too (keep strings but valid numbers)
  const shares = toSharesArray(next).map((s) => ({
    ...s,
    total: s?.total != null ? String(round2(toNum(s.total))) : s?.total,
    bookKeepingAmount: s?.bookKeepingAmount != null ? String(round2(toNum(s.bookKeepingAmount))) : s?.bookKeepingAmount,
  }));
  if (shares.length) next = setSharesArray(next, shares);

  return next;
};

const sanitizeInvoiceForSoap = (invoice: any, options?: { isCreate?: boolean }) => {
  // =======================================================
  // SECTION 7: SOAP Sanitize (Invoice + Transactions)
  // - Create-specific fallback normalization
  // - Share balancing pass
  // =======================================================
  let next = sanitizeLongFields(invoice, [
    "id",
    "treeHandle",
    "createdBy",
    "modifiedBy",
    "journal",
    "state",
    "provider",
    "userId",
    "customer",
    "shipTo",
    "project",
    "customerContact",
    "parent",
    "activity",
    "dimension1",
    "dimension2",
    "dimension3",
    "paymentMethod",
    "subject",
    "internalApprovalBy",
    "externalApprovalBy",
    "confirmedState",
    "nextState",
    "field0Id",
    "field1Id",
    "field2Id",
    "field3Id",
    "field4Id",
    "field5Id",
    "field6Id",
    "field7Id",
    "field8Id",
    "field9Id",
  ]);

  if (options?.isCreate) {
    const fallbackFolder =
      asLongId(next?.folder) ||
      asLongId(next?.customer) ||
      asLongId(next?.provider) ||
      asLongId(next?.shipTo) ||
      asLongId(next?.project) ||
      asLongId(next?.userId);

    if (fallbackFolder) next.folder = fallbackFolder;

    const fallbackSubject =
      asLongId(next?.subject) ||
      asLongId(next?.customer) ||
      asLongId(next?.provider) ||
      asLongId(next?.folder) ||
      fallbackFolder;

    if (fallbackSubject) next.subject = fallbackSubject;

    const fallbackTreeHandle =
      asLongId(next?.treeHandle) ||
      asLongId(next?.transactionTreeHandle) ||
      asLongId(next?.journalTreeHandle);

    if (fallbackTreeHandle) {
      next.treeHandle = fallbackTreeHandle;
      if (!asLongId(next?.transactionTreeHandle)) {
        next.transactionTreeHandle = fallbackTreeHandle;
      }
    }
  }

  if (Array.isArray(next.TRANSACTION)) {
    next.TRANSACTION = next.TRANSACTION
      // normalize shares (create) then balance shares (create + update)
      .map((txn: any) => (options?.isCreate ? normalizeTransactionSharesForCreate(txn, next) : txn))
      .map((txn: any) => balanceTransactionShares(txn, next))
      // ensure required folder/subject/treeHandle on create
      .map((txn: any) => {
        if (!options?.isCreate) return txn;

        const txFolder =
          asLongId(txn?.folder) ||
          asLongId(next?.folder) ||
          asLongId(next?.customer) ||
          asLongId(next?.provider) ||
          asLongId(next?.shipTo) ||
          asLongId(next?.project) ||
          asLongId(next?.userId);

        const txSubject =
          asLongId(txn?.subject) ||
          txFolder ||
          asLongId(txn?.folder) ||
          asLongId(next?.subject) ||
          asLongId(next?.customer) ||
          asLongId(next?.provider) ||
          asLongId(next?.folder);

        const txTreeHandle =
          asLongId(txn?.treeHandle) ||
          asLongId(next?.transactionTreeHandle) ||
          asLongId(next?.treeHandle);

        // also enforce share subject/folder/treeHandle (important for create)
        const shares = toSharesArray(txn).map((s) => ({
          ...s,
          treeHandle:
            asLongId(s?.treeHandle) ||
            txTreeHandle ||
            asLongId(next?.transactionTreeHandle) ||
            asLongId(next?.treeHandle),
          folder:
            asLongId(s?.folder) ||
            txFolder ||
            asLongId(next?.folder) ||
            asLongId(next?.customer) ||
            asLongId(next?.provider),
          subject:
            asLongId(s?.subject) ||
            asLongId(s?.folder) ||
            txSubject ||
            asLongId(next?.subject) ||
            resolveInvoiceSubject(next),
        }));

        return {
          ...txn,
          ...(txTreeHandle ? { treeHandle: txTreeHandle } : {}),
          ...(txFolder ? { folder: txFolder } : {}),
          ...(txSubject ? { subject: txSubject } : {}),
          ...(shares.length ? { SHARES: { SHARE: shares } } : {}),
        };
      })
      .map(sanitizeTransactionForSoap);
  }

  return next;
};

export const buildInvoicePayloadForSave = (invoice: any, options?: { isCreate?: boolean }) => {
  // =======================================================
  // SECTION 8: Final Save Payload Builder (Add/Edit)
  // - Recalculate strict transactions
  // - Apply sanitize
  // - Return payload + validation errors
  // =======================================================
  const subject = resolveInvoiceSubject(invoice);

  const { transactions, totals } = buildStrictTransactions(invoice, {
    // Add mode + Edit mode payloads must be runtime-calculated.
    // (Read mode is not using this save path.)
    useResponseAmounts: false,
  });

  const preserveToBePaid = isPartiallyPaidState(invoice);
  const calculatedToBePaid = String(totals.toBePaid);
  const existingToBePaid = String(invoice?.toBePaid ?? "").trim();

  const payload = sanitizeInvoiceForSoap(
    {
      ...invoice,
      subject: invoice?.subject || subject,
      TRANSACTION: transactions,
      total: totals.total,
      totalWithVAT: totals.totalWithVAT,
      // Keep existing toBePaid when partially paid; otherwise use calculated (with PDF sign for purchase)
      toBePaid: preserveToBePaid && existingToBePaid ? existingToBePaid : calculatedToBePaid,
    },
    options
  );

  const missingSubjectOnCreate =
    !!options?.isCreate &&
    (!asLongId(payload?.subject) ||
      (Array.isArray(payload?.TRANSACTION) &&
        payload.TRANSACTION.some((txn: any) => {
          const txSubjectOk = !!asLongId(txn?.subject);
          const shares = toSharesArray(txn);
          const shareSubjectMissing = shares.some((share: any) => !asLongId(share?.subject));
          return !txSubjectOk || shareSubjectMissing;
        })));

  return {
    payload,
    errors: [
      ...validateStrictTransactions(transactions),
      ...validateShareSums(payload?.TRANSACTION || transactions),
      ...(missingSubjectOnCreate
        ? ["Missing subject in invoice/transaction payload. Please select customer/provider/context before saving."]
        : []),
    ],
  };
};
